<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <button
          @click="$emit('cancel')"
          class="text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1"
        >
          <span>&larr;</span> Back to Rules
        </button>
        <h2 class="text-lg font-semibold text-gray-800">
          {{ isNew ? 'Create New Rule' : 'Edit Rule' }}
        </h2>
      </div>
      <div class="flex gap-3">
        <button
          v-if="!isNew"
          @click="$emit('test', rule)"
          class="px-4 py-2 border border-green-600 text-green-600 rounded font-medium hover:bg-green-50"
        >
          Test Rule
        </button>
        <button
          @click="save"
          :disabled="!canSave || saving"
          class="btn-primary"
        >
          {{ saving ? 'Saving...' : 'Save Rule' }}
        </button>
      </div>
    </div>

    <div class="grid grid-cols-12 gap-6">
      <!-- Left Column: Basic Info & Evidence -->
      <div class="col-span-5">
        <!-- Basic Info -->
        <div class="bg-gray-50 rounded border border-gray-200 p-4 mb-4">
          <h3 class="text-sm font-semibold text-gray-800 mb-3">Basic Information</h3>

          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">Rule Name *</label>
            <input
              v-model="form.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="e.g., Diabetes Suspect from Insulin"
            />
          </div>

          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="form.description"
              rows="2"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Optional description of the rule"
            ></textarea>
          </div>

          <!-- Suggested ICD Codes -->
          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">Suggested ICD Codes *</label>
            <div class="space-y-2 mb-2">
              <div
                v-for="(icd, index) in form.suggested_icd_codes"
                :key="index"
                class="flex items-center gap-2"
              >
                <input
                  v-model="icd.code"
                  type="text"
                  class="w-28 px-2 py-1 border border-gray-300 rounded text-sm font-mono"
                  placeholder="E11.65"
                />
                <input
                  v-model="icd.label"
                  type="text"
                  class="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="Type 2 DM with hyperglycemia"
                />
                <button
                  @click="removeIcdCode(index)"
                  class="text-red-500 hover:text-red-700 px-2"
                >
                  &times;
                </button>
              </div>
            </div>
            <button
              @click="addIcdCode"
              class="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              + Add ICD Code
            </button>
          </div>

          <!-- Optional HCC Reference -->
          <div class="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">HCC Reference</label>
              <input
                v-model.number="form.suspect_hcc"
                type="number"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                placeholder="e.g., 37 (optional)"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">HCC Label</label>
              <input
                v-model="form.suspect_hcc_label"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                placeholder="e.g., Diabetes with Complications"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Confidence Tier *</label>
              <select
                v-model="form.confidence_tier"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
              >
                <option value="very_high">Very High</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <div class="flex items-center mt-2">
                <input
                  type="checkbox"
                  v-model="form.is_active"
                  class="rounded border-gray-300 mr-2"
                />
                <span class="text-sm">Active</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Evidence -->
        <div class="bg-gray-50 rounded border border-gray-200 p-4">
          <h3 class="text-sm font-semibold text-gray-800 mb-3">Evidence</h3>

          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">Evidence Type *</label>
            <select
              v-model="form.evidence_type"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
            >
              <option value="definitional">Definitional - Attribute IS the condition</option>
              <option value="treatment">Treatment Proves Condition</option>
              <option value="lab">Lab Meets Diagnostic Criteria</option>
              <option value="staging">Clinical Staging</option>
              <option value="historical">Historical Validation</option>
              <option value="risk_factor">Risk Factor Association</option>
            </select>
          </div>

          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">Evidence Citation *</label>
            <textarea
              v-model="form.evidence_citation"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Clinical/regulatory citation explaining why rule is valid"
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Evidence Source</label>
            <input
              v-model="form.evidence_source"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="e.g., FDA Label, ADA Guidelines"
            />
          </div>
        </div>

        <!-- Validation Stats (read-only) -->
        <div v-if="!isNew && hasValidationStats" class="bg-gray-50 rounded border border-gray-200 p-4 mt-4">
          <h3 class="text-sm font-semibold text-gray-800 mb-3">Validation Statistics</h3>
          <div class="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span class="text-gray-500">Validation Year:</span>
              <span class="ml-2 font-medium">{{ rule.validation_year }}</span>
            </div>
            <div>
              <span class="text-gray-500">Sample Size:</span>
              <span class="ml-2 font-medium">{{ rule.validation_sample_size?.toLocaleString() }}</span>
            </div>
            <div>
              <span class="text-gray-500">Conversion Rate:</span>
              <span class="ml-2 font-medium">{{ (rule.conversion_rate * 100).toFixed(1) }}%</span>
            </div>
            <div>
              <span class="text-gray-500">Lift:</span>
              <span class="ml-2 font-medium">{{ rule.lift?.toFixed(1) }}x</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Condition Builder -->
      <div class="col-span-7">
        <div class="bg-gray-50 rounded border border-gray-200 p-4">
          <h3 class="text-sm font-semibold text-gray-800 mb-3">Conditions</h3>
          <p class="text-xs text-gray-500 mb-4">
            Define the conditions that must be met to identify a suspect. Conditions are evaluated against patient attributes.
          </p>

          <!-- Logic Toggle -->
          <div class="mb-4 flex items-center gap-2">
            <span class="text-sm text-gray-600">Match</span>
            <select
              v-model="form.condition.logic"
              class="px-3 py-1 border border-gray-300 rounded text-sm bg-white"
            >
              <option value="AND">ALL conditions (AND)</option>
              <option value="OR">ANY condition (OR)</option>
            </select>
          </div>

          <!-- Condition Rows -->
          <div class="space-y-2 mb-4">
            <div
              v-for="(cond, index) in form.condition.conditions"
              :key="index"
              class="flex items-center gap-2 bg-white p-2 rounded border"
            >
              <!-- Attribute Selector -->
              <select
                v-model="cond.workflow_id"
                class="flex-1 px-2 py-1 border border-gray-300 rounded text-sm bg-white"
              >
                <option :value="null">Select attribute...</option>
                <optgroup
                  v-for="category in attributesByCategory"
                  :key="category.name"
                  :label="category.name"
                >
                  <option
                    v-for="attr in category.attributes"
                    :key="attr.id"
                    :value="attr.workflow_id"
                  >
                    {{ attr.name }}
                  </option>
                </optgroup>
              </select>

              <!-- Operator -->
              <select
                v-model="cond.operator"
                class="w-20 px-2 py-1 border border-gray-300 rounded text-sm bg-white"
              >
                <option value="=">=</option>
                <option value="!=">!=</option>
                <option value=">">&gt;</option>
                <option value=">=">&gt;=</option>
                <option value="<">&lt;</option>
                <option value="<=">&lt;=</option>
              </select>

              <!-- Value -->
              <input
                v-model="cond.value"
                type="text"
                class="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="Value"
              />

              <!-- Remove Button -->
              <button
                @click="removeCondition(index)"
                class="text-red-500 hover:text-red-700 px-2"
                title="Remove condition"
              >
                &times;
              </button>
            </div>
          </div>

          <!-- Add Condition Button -->
          <button
            @click="addCondition"
            class="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            + Add Condition
          </button>

          <!-- JSON Preview -->
          <div class="mt-4">
            <div class="text-xs text-gray-500 mb-1">Condition JSON Preview:</div>
            <pre class="bg-gray-800 text-green-400 p-3 rounded text-xs overflow-x-auto">{{ JSON.stringify(form.condition, null, 2) }}</pre>
          </div>
        </div>

        <!-- Validation Errors -->
        <div v-if="validationErrors.length > 0" class="mt-4 p-4 bg-red-50 rounded border border-red-200">
          <div class="text-sm font-medium text-red-700 mb-2">Please fix the following errors:</div>
          <ul class="text-sm text-red-600 list-disc list-inside">
            <li v-for="(error, i) in validationErrors" :key="i">{{ error }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SuspectRuleEditor',
  props: {
    rule: {
      type: Object,
      required: true,
    },
    attributes: {
      type: Array,
      default: () => [],
    },
    isNew: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['save', 'cancel', 'test'],
  data() {
    return {
      form: this.initForm(),
      saving: false,
      validationErrors: [],
    };
  },
  computed: {
    attributesByCategory() {
      const categoryMap = {};
      this.attributes.forEach(attr => {
        if (!attr.category_name || attr.category_name === 'Metric Primitives') return;
        const categoryName = attr.category_name;
        if (!categoryMap[categoryName]) {
          categoryMap[categoryName] = {
            name: categoryName,
            attributes: [],
          };
        }
        categoryMap[categoryName].attributes.push(attr);
      });
      return Object.values(categoryMap).sort((a, b) => a.name.localeCompare(b.name));
    },
    hasValidationStats() {
      return this.rule.validation_year || this.rule.conversion_rate;
    },
    canSave() {
      return (
        this.form.name &&
        this.form.suggested_icd_codes.length > 0 &&
        this.form.evidence_type &&
        this.form.evidence_citation &&
        this.form.condition.conditions.length > 0
      );
    },
  },
  methods: {
    initForm() {
      return {
        name: this.rule.name || '',
        description: this.rule.description || '',
        condition: this.rule.condition || { logic: 'AND', conditions: [] },
        suggested_icd_codes: this.rule.suggested_icd_codes || [],
        suspect_hcc: this.rule.suspect_hcc || null,
        suspect_hcc_label: this.rule.suspect_hcc_label || '',
        evidence_type: this.rule.evidence_type || 'historical',
        evidence_citation: this.rule.evidence_citation || '',
        evidence_source: this.rule.evidence_source || '',
        confidence_tier: this.rule.confidence_tier || 'medium',
        is_active: this.rule.is_active !== undefined ? this.rule.is_active : true,
      };
    },

    addIcdCode() {
      this.form.suggested_icd_codes.push({ code: '', label: '' });
    },

    removeIcdCode(index) {
      this.form.suggested_icd_codes.splice(index, 1);
    },

    addCondition() {
      this.form.condition.conditions.push({
        workflow_id: null,
        operator: '=',
        value: '',
      });
    },

    removeCondition(index) {
      this.form.condition.conditions.splice(index, 1);
    },

    validate() {
      this.validationErrors = [];

      if (!this.form.name) {
        this.validationErrors.push('Rule name is required');
      }

      if (this.form.suggested_icd_codes.length === 0) {
        this.validationErrors.push('At least one ICD code is required');
      }

      this.form.suggested_icd_codes.forEach((icd, i) => {
        if (!icd.code) {
          this.validationErrors.push(`ICD code ${i + 1}: Code is required`);
        }
      });

      if (!this.form.evidence_citation) {
        this.validationErrors.push('Evidence citation is required');
      }

      if (this.form.condition.conditions.length === 0) {
        this.validationErrors.push('At least one condition is required');
      }

      this.form.condition.conditions.forEach((cond, i) => {
        if (!cond.workflow_id) {
          this.validationErrors.push(`Condition ${i + 1}: Select an attribute`);
        }
        if (!cond.value && cond.value !== 0) {
          this.validationErrors.push(`Condition ${i + 1}: Enter a value`);
        }
      });

      return this.validationErrors.length === 0;
    },

    save() {
      if (!this.validate()) return;

      this.saving = true;
      this.$emit('save', this.form);
      // The parent will handle the actual save and set saving to false on completion/error
      setTimeout(() => {
        this.saving = false;
      }, 2000);
    },
  },
  watch: {
    rule: {
      handler() {
        this.form = this.initForm();
      },
      deep: true,
    },
  },
};
</script>
